import { useState, type ChangeEvent } from 'react';
import { Button, FormLabel, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { BaseDialog } from '@/components/shared/base-dialog';

import { useNotification } from '@/context/notification';
import { useDecideResourceApproval } from '@/data/subsidy/resource-approvals/decision';

export type ConfirmDecisionDialogProps = {
  decision: 'approved' | 'rejected';
  onClose: () => void;
  id: string;
};

const dialogInfo = {
  approved: {
    title: 'Aprovar item',
    description:
      'Você está aprovando um item para reembolso. Após aprovação, o solicitante receberá um email de confirmação e seguirá para a etapa do envio de comprovantes.',
    confirmButtonText: 'Aprovar item',
  },
  rejected: {
    title: 'Recusar item',
    description:
      'Você está recusando o pedido de um item para reembolso. Para finalizar a ação, descreva o motivo abaixo e envie-o para o solicitante.',
    confirmButtonText: 'Recusar item',
  },
};

export const ConfirmDecisionDialog = ({
  decision,
  onClose,
  id,
}: ConfirmDecisionDialogProps) => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const { mutate: decideResourceApproval, isPending } =
    useDecideResourceApproval();
  const { toast } = useNotification();
  const navigate = useNavigate();

  const handleConfirm = () => {
    if (decision === 'rejected' && !comment.trim()) {
      setError('É obrigatório justificar a rejeição de uma solicitação');
      return;
    }

    decideResourceApproval(
      {
        decision,
        decision_comment: comment,
        resource_approval_uuid: id,
      },
      {
        onSuccess: () => {
          toast({
            content: 'Decisão registrada com sucesso!',
            type: 'success',
          });
          navigate('/empresa/gestao-de-reembolsos/detalhes/' + id);
          onClose();
        },
        onError: () => {
          toast({
            content:
              'Erro ao processar a solicitação. Tentar novamente em instantes.',
            type: 'error',
          });
        },
      }
    );
  };

  const handleCommentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
    if (e.target.value.trim()) {
      setError('');
    }
  };

  return (
    <BaseDialog
      open
      onClose={onClose}
      title={dialogInfo[decision].title}
      actionsComponent={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={decision === 'rejected' ? 'error' : 'success'}
            onClick={handleConfirm}
            loading={isPending}
          >
            {dialogInfo[decision].confirmButtonText}
          </Button>
        </>
      }
    >
      <Typography mb={2}>{dialogInfo[decision].description}</Typography>
      <FormLabel>Justificativa</FormLabel>
      <TextField
        placeholder="Escreva a justificativa aqui..."
        variant="outlined"
        fullWidth
        multiline
        rows={4}
        value={comment}
        error={!!error}
        helperText={error}
        required
        onChange={handleCommentChange}
      />
    </BaseDialog>
  );
};
